import { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatDate, formatMoney, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import type {
  BillingEvent,
  FondAufstellungZeile,
  Invoice,
  InvoiceDraft,
  InvoiceLine,
  RateCard,
  ServiceItem,
  TaxSubtotal,
} from '../types';
import { TAX_CATEGORY_LABEL } from '../types';

/** Aufstellung der versandten Anforderungen nach Fond - Grundlage, um die
 *  Lagergeld-Rechnung je Abteilung weiterzuverrechnen. */
function FondAufstellung({ zeilen }: { zeilen: FondAufstellungZeile[] }) {
  if (!zeilen || zeilen.length === 0) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <h4 style={{ marginBottom: 6 }}>Aufstellung nach Fond</h4>
      <div className="small muted" style={{ marginBottom: 8 }}>
        Versandte Anforderungen im Zeitraum, je Abteilung (Fond) - zur
        Weiterverrechnung.
      </div>
      <table>
        <thead>
          <tr>
            <th>Fond</th>
            <th className="num">Anforderungen</th>
            <th className="num">Menge</th>
            <th>Aufträge</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.fond_id ?? 'ohne'}>
              <td>{z.fond}</td>
              <td className="num">{z.anzahl}</td>
              <td className="num">{z.menge_gesamt}</td>
              <td className="small mono">{z.auftraege.map((a) => a.number).join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const firstOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Fasst die gespeicherten Positionen zu Steuergruppen zusammen. Die Steuer
 * steht je Position bereits fest - hier wird nur summiert, nicht neu gerechnet,
 * damit die Anzeige nicht von der festgeschriebenen Rechnung abweichen kann.
 */
function lineSubtotals(lines: InvoiceLine[]): TaxSubtotal[] {
  const groups = new Map<string, TaxSubtotal>();
  for (const line of lines) {
    const key = `${line.tax_category}-${line.tax_rate_percent}`;
    const group = groups.get(key) ?? {
      category: line.tax_category,
      ratePercent: line.tax_rate_percent,
      netCents: 0,
      taxCents: 0,
      exemptionReason: null,
    };
    group.netCents += line.amount_cents;
    group.taxCents += line.tax_cents;
    groups.set(key, group);
  }
  return [...groups.values()];
}

const MODELL_LABEL: Record<string, string> = {
  einheiten: 'nach Menge',
  stellplaetze: 'nach Stellplätzen',
  beides: 'Menge + Stellplätze',
};

/**
 * Beschriftung einer Steuergruppe. Bei 0 % steht die Kategorie im Vordergrund,
 * sonst der Satz - eine Zeile "Umsatzsteuer 0 %" wäre auf einer Reverse-Charge-
 * Rechnung irreführend.
 */
function taxLabel(group: TaxSubtotal): string {
  if (group.category === 'S') return `Umsatzsteuer ${group.ratePercent} %`;
  return TAX_CATEGORY_LABEL[group.category];
}

/** Tausendstel Cent als Euro-Text, mit drei Nachkommastellen bei Bruchteilen. */
function millicentsToEuro(millicents: number): string {
  const euro = millicents / 100000;
  return `${euro.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 5 })} €`;
}

export function BillingPage() {
  const { owners, can } = useAuth();
  const mayBill = can('admin', 'kaufmann');

  const [rates, setRates] = useState<RateCard[] | null>(null);
  const [events, setEvents] = useState<BillingEvent[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [erfassung, setErfassung] = useState({ ownerId: '', serviceId: '', qty: '1', note: '' });
  const [katalogForm, setKatalogForm] = useState<{
    ownerId: string;
    code: string;
    bezeichnung: string;
    einheit: string;
    preis: string;
  } | null>(null);
  const [openInvoice, setOpenInvoice] = useState<{
    invoice: Invoice;
    lines: InvoiceLine[];
    fondAufstellung?: FondAufstellungZeile[];
  } | null>(null);
  // Ein Storno braucht einen Grund - deshalb ein eigenes Feld statt eines
  // Knopfs, der sofort bucht.
  const [cancelReason, setCancelReason] = useState<string | null>(null);

  const [rateForm, setRateForm] = useState<{
    ownerId: string;
    model: 'einheiten' | 'stellplaetze' | 'beides';
    storage: string;
    slot: string;
    slots: string;
    slotPrice: string;
    handlingIn: string;
    handlingOut: string;
    minMonthly: string;
  } | null>(null);

  const [period, setPeriod] = useState({ ownerId: '', from: firstOfMonth(), to: today() });
  const [draft, setDraft] = useState<InvoiceDraft | null>(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [rateList, eventList, invoiceList, serviceList] = await Promise.all([
        mayBill ? api<RateCard[]>('/api/billing/rates') : Promise.resolve([]),
        api<BillingEvent[]>('/api/billing/events', { query: { openOnly: 'true', limit: 50 } }),
        api<Invoice[]>('/api/billing/invoices'),
        mayBill ? api<ServiceItem[]>('/api/billing/services') : Promise.resolve([]),
      ]);
      setRates(rateList);
      setEvents(eventList);
      setInvoices(invoiceList);
      setServices(serviceList);
    } catch (err) {
      setError(errorText(err));
    }
  }, [mayBill]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveRate() {
    if (!rateForm) return;
    setBusy(true);
    setError('');
    try {
      await api('/api/billing/rates', {
        method: 'PUT',
        body: {
          ownerId: rateForm.ownerId,
          storageModel: rateForm.model,
          storageMillicentsPerUnitDay: Math.round(Number(rateForm.storage.replace(',', '.')) || 0),
          // Eingabe in Euro je Platz und Tag, gespeichert in Tausendstel Cent.
          slotMillicentsPerDay: Math.round(Number(rateForm.slot.replace(',', '.')) * 100_000 || 0),
          contractedSlots: Math.round(Number(rateForm.slots.replace(',', '.')) || 0),
          contractedSlotCentsPerMonth: Math.round(
            Number(rateForm.slotPrice.replace(',', '.')) * 100 || 0,
          ),
          handlingInCents: Math.round(Number(rateForm.handlingIn.replace(',', '.')) * 100 || 0),
          handlingOutCents: Math.round(Number(rateForm.handlingOut.replace(',', '.')) * 100 || 0),
          minMonthlyCents: Math.round(Number(rateForm.minMonthly.replace(',', '.')) * 100 || 0),
        },
      });
      setMessage('Tarif gespeichert.');
      setRateForm(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function katalogSpeichern() {
    if (!katalogForm) return;
    setBusy(true);
    setError('');
    try {
      await api('/api/billing/services', {
        method: 'PUT',
        body: {
          ownerId: katalogForm.ownerId || null,
          code: katalogForm.code,
          bezeichnung: katalogForm.bezeichnung,
          einheit: katalogForm.einheit,
          preisCents: Math.round(Number(katalogForm.preis.replace(',', '.')) * 100 || 0),
        },
      });
      setMessage('Leistung im Katalog gespeichert.');
      setKatalogForm(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function leistungErfassen() {
    if (!erfassung.ownerId || !erfassung.serviceId) {
      return setError('Bitte Auftraggeber und Leistung w\u00e4hlen.');
    }
    setBusy(true);
    setError('');
    try {
      const ergebnis = await api<{ amountCents: number; bezeichnung: string }>(
        '/api/billing/services/record',
        {
          method: 'POST',
          body: {
            ownerId: erfassung.ownerId,
            serviceItemId: erfassung.serviceId,
            qty: Number(erfassung.qty.replace(',', '.')) || 0,
            note: erfassung.note || null,
          },
        },
      );
      setMessage(
        `${ergebnis.bezeichnung} erfasst \u2014 ${formatMoney(ergebnis.amountCents)}. ` +
          'Erscheint auf der n\u00e4chsten Rechnung des Zeitraums.',
      );
      setErfassung({ ...erfassung, qty: '1', note: '' });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function snapshot() {
    setBusy(true);
    try {
      await api('/api/billing/snapshot', { method: 'POST', body: { day: today() } });
      setMessage('Bestandsschnappschuss wurde als Hintergrundauftrag eingeplant.');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function backfill() {
    setBusy(true);
    try {
      const result = await api<{ written: number }>('/api/billing/snapshot/backfill', {
        method: 'POST',
        body: { from: period.from, to: period.to },
      });
      setMessage(`${result.written} Tagesschnappschüsse nachgetragen.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function preview() {
    if (!period.ownerId) return setError('Bitte einen Auftraggeber wählen.');
    setBusy(true);
    setError('');
    setDraft(null);
    try {
      setDraft(
        await api<InvoiceDraft>('/api/billing/invoices/preview', {
          method: 'POST',
          body: { ownerId: period.ownerId, periodStart: period.from, periodEnd: period.to },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    setBusy(true);
    setError('');
    try {
      const created = await api<Invoice>('/api/billing/invoices', {
        method: 'POST',
        body: { ownerId: period.ownerId, periodStart: period.from, periodEnd: period.to },
      });
      setMessage(`Rechnung ${created.number} über ${formatMoney(created.net_cents)} erstellt.`);
      setDraft(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function openDetail(id: string) {
    try {
      setOpenInvoice(
        await api<{ invoice: Invoice; lines: InvoiceLine[]; fondAufstellung?: FondAufstellungZeile[] }>(
          `/api/billing/invoices/${id}`,
        ),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function bookInvoice(id: string) {
    setBusy(true);
    try {
      await api(`/api/billing/invoices/${id}/book`, { method: 'POST' });
      setMessage('Rechnung festgeschrieben.');
      await load();
      await openDetail(id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function perMail(id: string) {
    setBusy(true);
    setError('');
    try {
      const bericht = await api<{ status: string; meldung: string }>(`/api/mail/invoices/${id}`, {
        method: 'POST',
        body: {},
      });
      if (bericht.status === 'fehler') setError(bericht.meldung);
      else setMessage(bericht.meldung);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function xrechnung(id: string) {
    setBusy(true);
    setError('');
    try {
      await downloadFile(`/api/accounting/invoices/${id}/xrechnung`);
      setMessage('XRechnung heruntergeladen.');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function zugferd(id: string) {
    setBusy(true);
    setError('');
    try {
      await downloadFile(`/api/accounting/invoices/${id}/zugferd`);
      setMessage('ZUGFeRD-PDF heruntergeladen.');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancelInvoice(id: string) {
    const reason = (cancelReason ?? '').trim();
    if (reason.length < 3) return setError('Bitte einen Stornogrund angeben.');
    setBusy(true);
    setError('');
    try {
      const storno = await api<Invoice>(`/api/billing/invoices/${id}/cancel`, {
        method: 'POST',
        body: { reason },
      });
      setMessage(
        `Storno ${storno.number} erzeugt. Die Leistungen des Zeitraums sind wieder abrechenbar.`,
      );
      setCancelReason(null);
      await load();
      await openDetail(storno.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function discard(id: string) {
    setBusy(true);
    try {
      await api(`/api/billing/invoices/${id}`, { method: 'DELETE' });
      setMessage('Entwurf verworfen, die Ereignisse sind wieder offen.');
      setOpenInvoice(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const billable = owners.filter((owner) => !owner.is_self);

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {mayBill && (
        <Card
          title="Tarife je Auftraggeber"
          actions={
            <button
              className="small"
              onClick={() =>
                setRateForm(
                  rateForm
                    ? null
                    : {
                        ownerId: '',
                        model: 'einheiten',
                        storage: '4',
                        slot: '0,90',
                        slots: '0',
                        slotPrice: '15,00',
                        handlingIn: '1,20',
                        handlingOut: '1,80',
                        minMonthly: '0',
                      },
                )
              }
            >
              {rateForm ? 'Abbrechen' : 'Tarif hinterlegen'}
            </button>
          }
          padded={false}
        >
          {rateForm && (
            <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
              <div className="form-grid">
                <label className="field">
                  <span>Auftraggeber</span>
                  <select
                    value={rateForm.ownerId}
                    onChange={(e) => setRateForm({ ...rateForm, ownerId: e.target.value })}
                  >
                    <option value="">— bitte wählen —</option>
                    {billable.map((owner) => (
                      <option key={owner.id} value={owner.id}>
                        {owner.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Lagergeldmodell</span>
                  <select
                    value={rateForm.model}
                    onChange={(e) =>
                      setRateForm({ ...rateForm, model: e.target.value as typeof rateForm.model })
                    }
                  >
                    <option value="einheiten">nach Menge (Pakete, Kartons, Stück)</option>
                    <option value="stellplaetze">nach Stellplätzen</option>
                    <option value="beides">beides nebeneinander</option>
                  </select>
                </label>
                {rateForm.model !== 'stellplaetze' && (
                  <TextField
                    label="Lagergeld"
                    value={rateForm.storage}
                    onChange={(v) => setRateForm({ ...rateForm, storage: v })}
                    hint="Tausendstel Cent je Einheit und Tag"
                  />
                )}
                {rateForm.model !== 'einheiten' && (
                  <>
                    <TextField
                      label="Fest gemietete Plätze"
                      value={rateForm.slots}
                      onChange={(v) => setRateForm({ ...rateForm, slots: v })}
                      hint="0 = kein Kontingent, dann zählt jeder belegte Platz"
                    />
                    <TextField
                      label="Miete je Platz und Monat (€)"
                      value={rateForm.slotPrice}
                      onChange={(v) => setRateForm({ ...rateForm, slotPrice: v })}
                      hint="wird tageweise anteilig berechnet"
                    />
                    <TextField
                      label="Platz und Tag über Kontingent (€)"
                      value={rateForm.slot}
                      onChange={(v) => setRateForm({ ...rateForm, slot: v })}
                      hint="ohne Kontingent: Preis für jeden belegten Platz"
                    />
                  </>
                )}
                <TextField
                  label="Handling Eingang (€)"
                  value={rateForm.handlingIn}
                  onChange={(v) => setRateForm({ ...rateForm, handlingIn: v })}
                  hint="je Vorgang"
                />
                <TextField
                  label="Handling Ausgang (€)"
                  value={rateForm.handlingOut}
                  onChange={(v) => setRateForm({ ...rateForm, handlingOut: v })}
                  hint="je Vorgang"
                />
                <TextField
                  label="Mindestumsatz (€)"
                  value={rateForm.minMonthly}
                  onChange={(v) => setRateForm({ ...rateForm, minMonthly: v })}
                  hint="0 = keiner"
                />
              </div>
              <button className="primary" onClick={saveRate} disabled={busy}>
                Tarif speichern
              </button>
            </div>
          )}

          {!rates ? (
            <Loading />
          ) : rates.length === 0 ? (
            <Empty>Noch kein Tarif hinterlegt — ohne Tarif entstehen keine Abrechnungsdaten.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Auftraggeber</th>
                  <th>Modell</th>
                  <th className="num">je Einheit/Tag</th>
                  <th className="num">Stellplätze</th>
                  <th className="num">Handling Ein</th>
                  <th className="num">Handling Aus</th>
                  <th className="num">Mindestumsatz</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((rate) => (
                  <tr key={rate.id}>
                    <td>{rate.owner_name}</td>
                    <td className="small">{MODELL_LABEL[rate.storage_model] ?? rate.storage_model}</td>
                    <td className="num">
                      {rate.storage_model === 'stellplaetze'
                        ? '—'
                        : millicentsToEuro(rate.storage_millicents_per_unit_day)}
                    </td>
                    <td className="num small">
                      {rate.storage_model === 'einheiten' ? (
                        '—'
                      ) : rate.contracted_slots > 0 ? (
                        <>
                          {rate.contracted_slots} fest à {formatMoney(rate.contracted_slot_cents_per_month)}/Mon.
                          <br />
                          darüber {millicentsToEuro(rate.slot_millicents_per_day)}/Tag
                        </>
                      ) : (
                        <>{millicentsToEuro(rate.slot_millicents_per_day)} je Platz/Tag</>
                      )}
                    </td>
                    <td className="num">{formatMoney(rate.handling_in_cents)}</td>
                    <td className="num">{formatMoney(rate.handling_out_cents)}</td>
                    <td className="num">{rate.min_monthly_cents ? formatMoney(rate.min_monthly_cents) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {mayBill && (
        <Card
          title="Zusatzleistungen"
          actions={
            <button
              className="small"
              onClick={() =>
                setKatalogForm(
                  katalogForm
                    ? null
                    : { ownerId: '', code: '', bezeichnung: '', einheit: 'Vorgang', preis: '0,00' },
                )
              }
            >
              {katalogForm ? 'Abbrechen' : 'Leistung anlegen'}
            </button>
          }
          padded={false}
        >
          <div className="body small muted" style={{ paddingBottom: 0 }}>
            Umpacken, Etikettieren, Palettentausch, Entsorgung — Leistungen, die anfallen, ohne
            dass eine Bestandsbewegung sie auslöst. Erfasst landen sie im selben Topf wie das
            Handling und erscheinen auf der nächsten Rechnung des Zeitraums.
          </div>

          {katalogForm && (
            <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
              <div className="form-grid">
                <label className="field">
                  <span>Gilt für</span>
                  <select
                    value={katalogForm.ownerId}
                    onChange={(e) => setKatalogForm({ ...katalogForm, ownerId: e.target.value })}
                  >
                    <option value="">alle Auftraggeber</option>
                    {billable.map((owner) => (
                      <option key={owner.id} value={owner.id}>
                        nur {owner.name}
                      </option>
                    ))}
                  </select>
                </label>
                <TextField
                  label="Kürzel"
                  value={katalogForm.code}
                  onChange={(v) => setKatalogForm({ ...katalogForm, code: v })}
                  hint="z. B. UMPACK"
                />
                <TextField
                  label="Bezeichnung"
                  value={katalogForm.bezeichnung}
                  onChange={(v) => setKatalogForm({ ...katalogForm, bezeichnung: v })}
                />
                <TextField
                  label="Einheit"
                  value={katalogForm.einheit}
                  onChange={(v) => setKatalogForm({ ...katalogForm, einheit: v })}
                />
                <TextField
                  label="Preis je Einheit (€)"
                  value={katalogForm.preis}
                  onChange={(v) => setKatalogForm({ ...katalogForm, preis: v })}
                />
              </div>
              <button className="primary small" onClick={katalogSpeichern} disabled={busy}>
                speichern
              </button>
            </div>
          )}

          {services.length === 0 ? (
            <Empty>Noch keine Zusatzleistungen im Katalog.</Empty>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Kürzel</th>
                    <th>Bezeichnung</th>
                    <th>Gilt für</th>
                    <th>Einheit</th>
                    <th className="num">Preis</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((leistung) => (
                    <tr key={leistung.id}>
                      <td className="mono small">{leistung.code}</td>
                      <td>{leistung.bezeichnung}</td>
                      <td className="small">
                        {leistung.owner_name ? (
                          <Badge kind="info">nur {leistung.owner_name}</Badge>
                        ) : (
                          <span className="muted">alle</span>
                        )}
                      </td>
                      <td className="small">{leistung.einheit}</td>
                      <td className="num">{formatMoney(leistung.preis_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="body" style={{ borderTop: '1px solid var(--line)' }}>
                <strong className="small">Leistung erfassen</strong>
                <div className="form-grid" style={{ marginTop: 8 }}>
                  <label className="field">
                    <span>Auftraggeber</span>
                    <select
                      value={erfassung.ownerId}
                      onChange={(e) => setErfassung({ ...erfassung, ownerId: e.target.value })}
                    >
                      <option value="">— wählen —</option>
                      {billable.map((owner) => (
                        <option key={owner.id} value={owner.id}>
                          {owner.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Leistung</span>
                    <select
                      value={erfassung.serviceId}
                      onChange={(e) => setErfassung({ ...erfassung, serviceId: e.target.value })}
                    >
                      <option value="">— wählen —</option>
                      {services
                        .filter(
                          (leistung) =>
                            leistung.aktiv &&
                            (leistung.owner_id === null || leistung.owner_id === erfassung.ownerId),
                        )
                        .map((leistung) => (
                          <option key={leistung.id} value={leistung.id}>
                            {leistung.bezeichnung} ({formatMoney(leistung.preis_cents)}
                            {leistung.owner_name ? ', Sonderpreis' : ''})
                          </option>
                        ))}
                    </select>
                  </label>
                  <TextField
                    label="Menge"
                    value={erfassung.qty}
                    onChange={(v) => setErfassung({ ...erfassung, qty: v })}
                  />
                  <TextField
                    label="Vermerk"
                    value={erfassung.note}
                    onChange={(v) => setErfassung({ ...erfassung, note: v })}
                    hint="optional, erscheint auf der Rechnung"
                  />
                </div>
                <button className="primary small" onClick={leistungErfassen} disabled={busy}>
                  erfassen
                </button>
              </div>
            </>
          )}
        </Card>
      )}

      {mayBill && (
        <Card title="Rechnung erstellen">
          <p className="small muted" style={{ marginTop: 0 }}>
            Lagergeld entsteht aus den Tagesschnappschüssen, Handling aus den Bestandsbewegungen.
            Fehlen Schnappschüsse für den Zeitraum, lassen sie sich aus dem Bewegungsjournal
            nachtragen.
          </p>
          <div className="form-grid">
            <label className="field">
              <span>Auftraggeber</span>
              <select
                value={period.ownerId}
                onChange={(e) => setPeriod({ ...period, ownerId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {billable.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Von"
              type="date"
              value={period.from}
              onChange={(v) => setPeriod({ ...period, from: v })}
            />
            <TextField
              label="Bis"
              type="date"
              value={period.to}
              onChange={(v) => setPeriod({ ...period, to: v })}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="primary" onClick={preview} disabled={busy}>
              Vorschau rechnen
            </button>
            <button onClick={backfill} disabled={busy}>
              Schnappschüsse nachtragen
            </button>
            <button onClick={snapshot} disabled={busy}>
              Schnappschuss für heute
            </button>
          </div>

          {draft && (
            <>
              <table style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>Position</th>
                    <th className="num">Menge</th>
                    <th>Einheit</th>
                    <th className="num">Einzelpreis</th>
                    <th className="num">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.description}</td>
                      <td className="num">{formatQty(line.qty)}</td>
                      <td className="small">{line.unit}</td>
                      <td className="num">{millicentsToEuro(line.unitPriceMillicents)}</td>
                      <td className="num">{formatMoney(line.amountCents)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right' }}>
                      Netto
                    </td>
                    <td className="num">{formatMoney(draft.netCents)}</td>
                  </tr>
                  {draft.taxSubtotals.map((group) => (
                    <tr key={`${group.category}-${group.ratePercent}`}>
                      <td colSpan={4} style={{ textAlign: 'right' }}>
                        {taxLabel(group)} auf {formatMoney(group.netCents)}
                      </td>
                      <td className="num">{formatMoney(group.taxCents)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>
                      Zahlbetrag
                    </td>
                    <td className="num" style={{ fontWeight: 600 }}>
                      {formatMoney(draft.grossCents)}
                    </td>
                  </tr>
                </tbody>
              </table>
              {draft.taxNote && <div className="small muted" style={{ marginTop: 8 }}>{draft.taxNote}</div>}
              <FondAufstellung zeilen={draft.fondAufstellung} />
              <button className="primary" onClick={create} disabled={busy} style={{ marginTop: 12 }}>
                Rechnung erstellen
              </button>
            </>
          )}
        </Card>
      )}

      <div className="grid two">
        <Card title="Rechnungen" padded={false}>
          {invoices.length === 0 ? (
            <Empty>Noch keine Rechnungen.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th>Auftraggeber</th>
                  <th>Zeitraum</th>
                  <th>Status</th>
                  <th className="num">Netto</th>
                  <th className="num">Brutto</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="mono">{invoice.number}</td>
                    <td className="small">{invoice.owner_name}</td>
                    <td className="small">
                      {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
                    </td>
                    <td>
                      <Badge
                        kind={
                          invoice.status === 'storniert'
                            ? 'bad'
                            : invoice.status === 'festgeschrieben'
                              ? 'ok'
                              : 'warn'
                        }
                      >
                        {invoice.status}
                      </Badge>
                    </td>
                    <td className="num">{formatMoney(invoice.net_cents)}</td>
                    <td className="num">{formatMoney(invoice.gross_cents)}</td>
                    <td className="num">
                      <button className="small ghost" onClick={() => openDetail(invoice.id)}>
                        öffnen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Offene Abrechnungsereignisse" padded={false}>
          {events.length === 0 ? (
            <Empty>Nichts offen.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Auftraggeber</th>
                    <th>Leistung</th>
                    <th className="num">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td className="small">{formatDate(event.event_date)}</td>
                      <td className="small">{event.owner_name}</td>
                      <td>{event.description}</td>
                      <td className="num">{formatMoney(event.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {openInvoice && (
        <Card
          title={`Rechnung ${openInvoice.invoice.number}`}
          actions={
            <>
              {openInvoice.invoice.status === 'festgeschrieben' && (
                <>
                  <button
                    className="small"
                    onClick={() => perMail(openInvoice.invoice.id)}
                    disabled={busy}
                  >
                    per E-Mail
                  </button>
                  <button
                    className="small"
                    onClick={() => zugferd(openInvoice.invoice.id)}
                    disabled={busy}
                  >
                    ZUGFeRD-PDF
                  </button>
                  <button
                    className="small"
                    onClick={() => xrechnung(openInvoice.invoice.id)}
                    disabled={busy}
                  >
                    XRechnung (XML)
                  </button>
                </>
              )}
              {mayBill &&
                openInvoice.invoice.status === 'festgeschrieben' &&
                !openInvoice.invoice.cancelled_by_invoice_id &&
                !openInvoice.invoice.cancels_invoice_id && (
                  <button
                    className="small"
                    onClick={() => setCancelReason(cancelReason === null ? '' : null)}
                    disabled={busy}
                  >
                    {cancelReason === null ? 'stornieren' : 'Storno abbrechen'}
                  </button>
                )}
              {mayBill && openInvoice.invoice.status === 'entwurf' && (
                <>
                  <button
                    className="primary small"
                    onClick={() => bookInvoice(openInvoice.invoice.id)}
                    disabled={busy}
                  >
                    festschreiben
                  </button>
                  <button className="small" onClick={() => discard(openInvoice.invoice.id)} disabled={busy}>
                    verwerfen
                  </button>
                </>
              )}
              <button className="small" onClick={() => setOpenInvoice(null)}>
                schliessen
              </button>
            </>
          }
          padded={false}
        >
          <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
            <div className="small muted">
              {openInvoice.invoice.owner_name} · {formatDate(openInvoice.invoice.period_start)} bis{' '}
              {formatDate(openInvoice.invoice.period_end)}
              {openInvoice.invoice.booked_at ? ' · festgeschrieben' : ' · Entwurf'}
              {openInvoice.invoice.due_date ? ` · zahlbar bis ${formatDate(openInvoice.invoice.due_date)}` : ''}
              {openInvoice.invoice.datev_export_id ? ' · an DATEV übergeben' : ''}
            </div>

            {openInvoice.invoice.cancelled_by_number && (
              <div style={{ marginTop: 8 }}>
                <Badge kind="bad">storniert</Badge>{' '}
                <span className="small">
                  aufgehoben durch {openInvoice.invoice.cancelled_by_number}
                  {openInvoice.invoice.cancel_reason ? ` · ${openInvoice.invoice.cancel_reason}` : ''}
                </span>
              </div>
            )}
            {openInvoice.invoice.cancels_number && (
              <div style={{ marginTop: 8 }}>
                <Badge kind="warn">Stornobeleg</Badge>{' '}
                <span className="small">
                  hebt {openInvoice.invoice.cancels_number} auf
                  {openInvoice.invoice.cancel_reason ? ` · ${openInvoice.invoice.cancel_reason}` : ''}
                </span>
              </div>
            )}

            {cancelReason !== null && (
              <div style={{ marginTop: 12 }}>
                <TextField
                  label="Stornogrund"
                  value={cancelReason}
                  onChange={setCancelReason}
                  placeholder="z. B. falscher Zeitraum abgerechnet"
                />
                <div className="small muted" style={{ margin: '4px 0 8px' }}>
                  Die Rechnung bleibt erhalten. Es entsteht ein eigener Stornobeleg mit
                  negativen Beträgen; der Zeitraum ist danach wieder abrechenbar.
                </div>
                <button
                  className="primary small"
                  onClick={() => cancelInvoice(openInvoice.invoice.id)}
                  disabled={busy}
                >
                  Storno buchen
                </button>
              </div>
            )}
          </div>
          <table>
            <thead>
              <tr>
                <th>Pos</th>
                <th>Position</th>
                <th className="num">Menge</th>
                <th>Einheit</th>
                <th className="num">Einzelpreis</th>
                <th>Steuer</th>
                <th className="num">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {openInvoice.lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.position}</td>
                  <td>{line.description}</td>
                  <td className="num">{formatQty(line.qty)}</td>
                  <td className="small">{line.unit}</td>
                  <td className="num">{millicentsToEuro(line.unit_price_millicents)}</td>
                  <td className="small">
                    {line.tax_category === 'S'
                      ? `${line.tax_rate_percent} %`
                      : TAX_CATEGORY_LABEL[line.tax_category]}
                  </td>
                  <td className="num">{formatMoney(line.amount_cents)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={6} style={{ textAlign: 'right' }}>
                  Netto
                </td>
                <td className="num">{formatMoney(openInvoice.invoice.net_cents)}</td>
              </tr>
              {lineSubtotals(openInvoice.lines).map((group) => (
                <tr key={`${group.category}-${group.ratePercent}`}>
                  <td colSpan={6} style={{ textAlign: 'right' }}>
                    {taxLabel(group)} auf {formatMoney(group.netCents)}
                  </td>
                  <td className="num">{formatMoney(group.taxCents)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={6} style={{ textAlign: 'right', fontWeight: 600 }}>
                  Zahlbetrag
                </td>
                <td className="num" style={{ fontWeight: 600 }}>
                  {formatMoney(openInvoice.invoice.gross_cents)}
                </td>
              </tr>
            </tbody>
          </table>
          {openInvoice.invoice.tax_note && (
            <div className="body small muted">{openInvoice.invoice.tax_note}</div>
          )}
          {openInvoice.fondAufstellung && openInvoice.fondAufstellung.length > 0 && (
            <div className="body">
              <FondAufstellung zeilen={openInvoice.fondAufstellung} />
            </div>
          )}
        </Card>
      )}
    </>
  );
}
